import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { batchElementId } from '@aytm/app/stats/page/components/lib/utils'
import { pressable } from '@aytm/app/stats/components/lib/pressable'
import { calcUncollapsedKeys } from '@aytm/app/stats/page/question-views/components/lib/uncollapsed-param'

import { buildAnswerVariantProps } from '@aytm/app/stats/page/question-views/hm/lib/props/variants'

import ItemsList from '@aytm/app/stats/page/question-views/components/items-list/ItemsList'
import { ItemBody, ItemHeader } from '@aytm/app/stats/page/question-views/oex/components/Item'

import style from '@aytm/app/stats/page/question-views/oex/components/styles/Items.module.scss'

// The answer-variant list of a heatmap: one collapsible row per pin or cluster carrying verbatims,
// each expanding to that row's own open-ended block. Replaces heatmap/answer_variants.coffee and
// the sub-item view under it; the rows are the extended open-ended ones, since a heatmap draws
// exactly the same thing.
//
// Controlled, like every other sub-items list: which rows are open lives with the shell, which
// owns a set of report params per row and resolves the params handed over here.
const Items = ({
  items = [],
  statType,
  uncollapsed,
  questionId,
  questionKey,
  type,
  format,
  components,
  answer,
  clickable,
  questionChartMode,
  defaultSortOrder,
  reportPercentDecimals,
  modelDefaults,
  link,
  isReportXpert,
  answerVariantParams,
  loadingRows,
  commentsScopeOf,
  handlers,
  onToggle,
  onToggleAll
}) => {
  // The props an open row's block draws from. It is a question of its own as far as the report
  // params go, so it announces its own changes and the shell writes them down.
  const oePropsOf = useMemo(
    () =>
      buildAnswerVariantProps({
        questionId,
        questionKey,
        type,
        statType,
        format,
        components,
        answer,
        modelDefaults,
        clickable,
        questionChartMode,
        defaultSortOrder,
        reportPercentDecimals,
        link,
        isReportXpert,
        answerVariantParams,
        loadingRows,
        commentsScopeOf,
        handlers
      }),
    [
      questionId,
      questionKey,
      type,
      statType,
      format,
      components,
      answer,
      modelDefaults,
      clickable,
      questionChartMode,
      defaultSortOrder,
      reportPercentDecimals,
      link,
      isReportXpert,
      answerVariantParams,
      loadingRows,
      commentsScopeOf,
      handlers
    ]
  )

  const openKeys = useMemo(() => calcUncollapsedKeys(uncollapsed), [uncollapsed])
  const openSet = useMemo(() => new Set(openKeys), [openKeys])

  const allOpen = 0 < items.length && openKeys.length === items.length

  // the list draws pins or clusters depending on the stat type, so the toggle names what it holds
  const collapseLabel = 'clusters' === statType ? 'Collapse clusters' : 'Collapse answers'
  const expandLabel = 'clusters' === statType ? 'Expand clusters' : 'Expand answers'

  const onToggleAllClick = event => {
    event.preventDefault()
    onToggleAll(allOpen)
  }

  return (
    <>
      {1 < items.length ? (
        <div className={style.toggleRow}>
          <span className={style.toggleAll} aria-expanded={allOpen} {...pressable(onToggleAllClick)}>
            {allOpen ? collapseLabel : expandLabel}
          </span>
        </div>
      ) : null}

      <ItemsList
        items={items}
        itemKey={item => item.position}
        itemId={item => batchElementId(questionId, item.position)}
        openKeys={openKeys}
        renderHeader={(item, { collapsed }) => (
          <ItemHeader item={item} answerLabels="hide" collapsed={collapsed} expandable />
        )}
        // a row loads its verbatims when it is opened, and gives them up again when it is closed
        renderBody={item => <ItemBody oeProps={openSet.has(String(item.position)) ? oePropsOf(item) : null} />}
        onToggle={onToggle}
      />
    </>
  )
}

Items.displayName = 'HeatmapItems'

Items.propTypes = {
  // the pins or clusters worth expanding, already drawn as answer variants by the shell
  items: PropTypes.array,
  // which stat the heatmap is being read by: its pins, or the clusters they were grouped into
  statType: PropTypes.string,
  uncollapsed: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  type: PropTypes.string,
  format: PropTypes.object,
  components: PropTypes.object,
  answer: answerPropType,
  clickable: PropTypes.bool,
  questionChartMode: PropTypes.string,
  defaultSortOrder: PropTypes.string,
  modelDefaults: PropTypes.object,
  link: PropTypes.string,
  isReportXpert: PropTypes.bool,
  // the report params every row owns, keyed by position
  answerVariantParams: PropTypes.object,
  // the rows whose own fetch is out, by position
  loadingRows: PropTypes.instanceOf(Set),
  commentsScopeOf: PropTypes.func,
  handlers: PropTypes.object,
  onToggle: PropTypes.func,
  onToggleAll: PropTypes.func
}

export default Items
